import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Chip,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const cardTypes = [
  { value: "Raw", label: "Raw" },
  { value: "Graded", label: "Graded" },
  { value: "Sleeved", label: "Sleeved" },
];

const statusOptions = ["all", "pending", "scraping", "scraped", "failed"];

function CustomToolbar({ onScrapeSession }) {
  return (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: "card_search_inputs" }} />
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<PlayArrowIcon />}
        sx={{ ml: 2 }}
        onClick={onScrapeSession}
      >
        Scrape Session
      </Button>
    </GridToolbarContainer>
  );
}

export default function CardSearchInputs() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [cardName, setCardName] = useState("");
  const [cardType, setCardType] = useState(cardTypes[0].value);
  const [sessionName, setSessionName] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [sessionFilter, setSessionFilter] = useState("");
  const [sessionIdFilter, setSessionIdFilter] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/card-search-inputs?show_all=true");
      if (!res.ok) throw new Error("Failed to fetch data");
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error(err);
      alert("Error loading card search inputs");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (showHistory) fetchData();
  }, [showHistory]);

  const updateRowStatus = (rowId, newStatus) => {
    setData((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, status: newStatus } : row
      )
    );
  };

  const updateRowExclusion = async (id, newValue) => {
    try {
      await fetch(`/api/update-exclusion/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exclusion_terms: newValue }),
      });
      setData((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, exclusion_terms: newValue } : row
        )
      );
    } catch (err) {
      console.error(err);
      alert("Failed to update exclusion terms");
    }
  };

  const handleAdd = async () => {
    if (!cardName.trim()) {
      alert("Card Name is required");
      return;
    }

    try {
      const res = await fetch("/api/card-search-inputs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_name: cardName,
          search_type: cardType,
          session_name: sessionName || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to add");
      setCardName("");
      setCardType(cardTypes[0].value);
      setSessionName("");
      if (showHistory) await fetchData();
      else setShowHistory(true);
    } catch (err) {
      console.error(err);
      alert("Error adding card search input");
    }
  };

  const triggerScrape = async (row) => {
    updateRowStatus(row.id, "scraping");

    try {
      const res = await fetch("/api/run-scraper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: row.id,
          card_name: row.card_name,
          url: row.auto_generated_ebay_url,
        }),
      });

      const json = await res.json();

      if (res.ok && json.success) {
        updateRowStatus(row.id, "scraped");
      } else {
        updateRowStatus(row.id, "failed");
        const msg = json.message || json.error || "Scrape failed";
        alert(`${row.card_name}: ${msg}`);
      }
    } catch (err) {
      updateRowStatus(row.id, "failed");
      console.error(err);
      alert(`Scraper error for ${row.card_name}`);
    }
  };

  const triggerScrapeSession = async () => {
    const visibleRows = filteredRows();
    if (!visibleRows.length) return alert("No rows to scrape");

    for (const row of visibleRows) {
      updateRowStatus(row.id, "scraping");
    }

    try {
      const res = await fetch("/api/run-scraper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_session_id: visibleRows[0].search_session_id }),
      });

      const json = await res.json();

      if (res.ok && json.success) {
        for (const row of visibleRows) {
          updateRowStatus(row.id, "scraped");
        }
      } else {
        for (const row of visibleRows) {
          updateRowStatus(row.id, "failed");
        }
        const msg = json.message || json.error || "Session scrape failed";
        alert(msg);
      }
    } catch (err) {
      for (const row of visibleRows) {
        updateRowStatus(row.id, "failed");
      }
      console.error(err);
      alert("Session scraper failed");
    }
  };

  const renderStatusChip = (status) => {
    const colorMap = {
      pending: "default",
      scraping: "warning",
      scraped: "success",
      failed: "error",
    };
    return (
      <Chip
        label={status}
        color={colorMap[status] || "default"}
        size="small"
      />
    );
  };

  const filteredRows = () => {
    return data.filter((row) => {
      return (
        (statusFilter === "all" || row.status === statusFilter) &&
        (!sessionFilter || row.session_name === sessionFilter) &&
        (!sessionIdFilter || row.search_session_id?.toString() === sessionIdFilter)
      );
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "card_name", headerName: "Card Name", width: 200 },
    { field: "search_type", headerName: "Type", width: 100 },
    {
      field: "session_name",
      headerName: "Session Name",
      width: 160,
    },
    {
      field: "search_session_id",
      headerName: "Session ID",
      width: 120,
    },
    {
      field: "auto_generated_ebay_url",
      headerName: "eBay URL",
      width: 200,
      renderCell: (params) => (
        <a href={params.value} target="_blank" rel="noopener noreferrer">
          View
        </a>
      ),
    },
    {
      field: "exclusion_terms",
      headerName: "Exclusions",
      width: 200,
      editable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => renderStatusChip(params.value),
    },
    {
      field: "actions",
      type: "actions",
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PlayArrowIcon />}
          label="Scrape"
          onClick={() => triggerScrape(params.row)}
        />,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Card Search Inputs
      </Typography>

      <Box
        component="form"
        sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
        onSubmit={(e) => {
          e.preventDefault();
          handleAdd();
        }}
      >
        <TextField
          label="Card Name"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          required
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          label="Type"
          value={cardType}
          onChange={(e) => setCardType(e.target.value)}
          sx={{ width: 150 }}
        >
          {cardTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Session Name"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          sx={{ width: 200 }}
        />
        <Button variant="contained" color="primary" type="submit">
          Add
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={showHistory}
            onChange={() => setShowHistory(!showHistory)}
            color="primary"
          />
        }
        label="Show Previous Search Sessions"
      />

      {showHistory && (
        <>
          <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 1 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Session Name"
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
            />

            <TextField
              label="Session ID"
              value={sessionIdFilter}
              onChange={(e) => setSessionIdFilter(e.target.value)}
              type="number"
            />
          </Box>

          <div style={{ height: 600, width: "100%", marginTop: 8 }}>
            <DataGrid
              rows={filteredRows()}
              columns={columns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              onCellEditCommit={(params) => {
                if (params.field === "exclusion_terms") {
                  updateRowExclusion(params.id, params.value);
                }
              }}
              components={{
                Toolbar: () => (
                  <CustomToolbar onScrapeSession={triggerScrapeSession} />
                ),
              }}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </div>
        </>
      )}
    </Paper>
  );
}
